import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BriefcaseIcon, CheckIcon, PlusIcon, StoreIcon, SunsetIcon, Trash2Icon, UserIcon } from 'lucide-react';
import { StepFrame, FieldGrid, Callout } from '../StepFrame';
import { ChoiceCard } from '../../ui/ChoiceCard';
import { HelpPanel } from '../../ui/HelpPanel';
import { TextField } from '../../ui/TextField';
import { Button } from '../../ui/Button';
import { AnimatedNumber } from '../../ui/AnimatedNumber';
import { usePortal } from '../../../contexts/PortalContext';
import { formatAmountInput, parseAmount, uid, yearsSince } from '../../../utils/format';
import type { Employer } from '../../../types/application';
const ease = [0.23, 1, 0.32, 1] as const;
export function EmploymentStep() {
  const {
    answers,
    update
  } = usePortal();
  return <StepFrame title="How do you earn your income?" help={<HelpPanel question="Why do you need my employment history?" data-id="element-249" data-mp="3b6ghzzs6l3j5">
          Lenders look for steady income over roughly the last two years. It doesn’t need to be the same job — changing jobs is completely normal.
        </HelpPanel>} data-id="element-248" data-mp="36858jq5ssv1g">
      <div role="radiogroup" className="grid gap-3 md:grid-cols-2" data-id="element-250" data-mp="2lbvbyhhja239">
        <ChoiceCard layout="stack" icon={<BriefcaseIcon className="h-5 w-5" data-id="element-252" data-mp="3008btpjqs531" />} title="I work for a company" description="You get a W-2." selected={answers.employmentType === 'w2'} onSelect={() => update({
        employmentType: 'w2'
      })} data-id="element-251" data-mp="2zzzvfnoeuhn3" />
        <ChoiceCard layout="stack" icon={<StoreIcon className="h-5 w-5" data-id="element-254" data-mp="22diy4ifog41c" />} title="I’m self-employed" description="You own 25% or more of a business." selected={answers.employmentType === 'self'} onSelect={() => update({
        employmentType: 'self'
      })} data-id="element-253" data-mp="27egfwtwryw2b" />
        <ChoiceCard layout="stack" icon={<SunsetIcon className="h-5 w-5" data-id="element-256" data-mp="2c9vszjmyknxo" />} title="I’m retired" description="Pension, Social Security, or retirement income." selected={answers.employmentType === 'retired'} onSelect={() => update({
        employmentType: 'retired'
      })} data-id="element-255" data-mp="2cb76wca4bc2i" />
        <ChoiceCard layout="stack" icon={<UserIcon className="h-5 w-5" data-id="element-258" data-mp="2kg80s5wd3c2p" />} title="Not currently working" description="You have other income, or none." selected={answers.employmentType === 'none'} onSelect={() => update({
        employmentType: 'none'
      })} data-id="element-257" data-mp="3nsyfzclelk2r" />
      </div>
    </StepFrame>;
}
function blankEmployer(): Employer {
  return {
    id: uid('e'),
    name: '',
    position: '',
    startDate: '',
    endDate: '',
    current: false,
    basePay: '',
    payFrequency: 'annual',
    bonus: '',
    commission: '',
    overtime: ''
  };
}
export function EmployerStep() {
  const {
    answers,
    update
  } = usePortal();
  const employers = answers.employers;
  const setEmployer = (id: string, patch: Partial<Employer>) => update({
    employers: employers.map(e => e.id === id ? {
      ...e,
      ...patch
    } : e)
  });
  const years = employers.reduce((s, e) => s + (e.startDate ? Math.min(yearsSince(e.startDate), 10) - (e.endDate ? yearsSince(e.endDate) : 0) : 0), 0);
  const self = answers.employmentType === 'self';
  return <StepFrame title={self ? 'Tell us about your business' : 'Where do you work?'} description="Start with your current job. We need about 2 years of history in total." data-id="element-259" data-mp="1myhni6mhhlb2">
      <div className="space-y-4" data-id="element-260" data-mp="1ai86vz5e6dtb">
        <AnimatePresence initial={false} data-id="element-261" data-mp="1afvt7y6o2pm2">
          {employers.map((e, i) => <motion.section key={e.id} layout initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.24,
          ease
        }} className="rounded-2xl bg-white p-4 ring-1 ring-ink-200 md:p-5" aria-label={`Employer ${i + 1}`} data-id="element-262" data-mp="16i7t8gq4a6nb" data-mpi={i}>
              <div className="mb-4 flex items-center justify-between" data-id="element-263" data-mp="mq1grhq9f32c">
                <h2 className="text-sm font-semibold text-ink-900" data-id="element-264" data-mp="iru1krbg29t9">{i === 0 ? self ? 'Your business' : 'Current employer' : 'Previous employer'}</h2>
                {i > 0 && <button onClick={() => update({
              employers: employers.filter(x => x.id !== e.id)
            })} className="grid h-8 w-8 place-items-center rounded-lg text-ink-400 transition-colors duration-150 hover:bg-ink-100 hover:text-critical-500" aria-label="Remove employer" data-id="element-265" data-mp="8te66c5dwedy">
                    <Trash2Icon className="h-4 w-4" data-id="element-266" data-mp="1z52j4gsankg3" />
                  </button>}
              </div>
              <FieldGrid data-id="element-267" data-mp="24344x9h3txv9">
                <TextField label={self ? 'Business name' : 'Employer'} value={e.name} onChange={v => setEmployer(e.id, {
              name: v
            })} data-id="element-268" data-mp="242b56sgkc8mm" />
                <TextField label={self ? 'Your role' : 'Job title'} value={e.position} onChange={v => setEmployer(e.id, {
              position: v
            })} data-id="element-269" data-mp="1q6h3ate7ijj0" />
                <TextField label="Start date" type="date" value={e.startDate} onChange={v => setEmployer(e.id, {
              startDate: v
            })} data-id="element-270" data-mp="1g6ypkuow3tze" />
                {i > 0 && <TextField label="End date" type="date" value={e.endDate} onChange={v => setEmployer(e.id, {
              endDate: v
            })} data-id="element-271" data-mp="1l74als0nmi8i" />}
              </FieldGrid>
            </motion.section>)}
        </AnimatePresence>
      </div>

      <AnimatePresence data-id="element-272" data-mp="13vfqnmj6kx8l">
        {employers.length > 0 && years > 0 && years < 2 && <motion.div initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="mt-4" data-id="element-273" data-mp="ywmb7okc2w5s">
            <Callout tone="warning" data-id="element-274" data-mp="1dn1yra082g74">That’s about {years.toFixed(1)} years so far. Add your previous employer so we have 2 years of history.</Callout>
          </motion.div>}
      </AnimatePresence>

      <Button variant="secondary" className="mt-4" icon={<PlusIcon className="h-4 w-4" data-id="element-276" data-mp="1hnlxgi0g1rvd" />} onClick={() => update({
      employers: [...employers, blankEmployer()]
    })} data-id="element-275" data-mp="1mkcbz0wlofeo">
        {employers.length ? 'Add another employer' : 'Add employer'}
      </Button>
    </StepFrame>;
}
function monthlyFor(e: Employer): number {
  const base = parseAmount(e.basePay);
  const m = e.payFrequency === 'annual' ? base / 12 : e.payFrequency === 'hourly' ? base * 2080 / 12 : base;
  return m + (parseAmount(e.bonus) + parseAmount(e.commission) + parseAmount(e.overtime)) / 12;
}
const extras = [{
  key: 'bonus',
  label: 'Bonus'
}, {
  key: 'commission',
  label: 'Commission'
}, {
  key: 'overtime',
  label: 'Overtime'
}] as const;
export function PayStep() {
  const {
    answers,
    update
  } = usePortal();
  const [shown, setShown] = React.useState<Record<string, boolean>>({});
  const current = answers.employers.filter(e => !e.endDate);
  const setEmployer = (id: string, patch: Partial<Employer>) => update({
    employers: answers.employers.map(e => e.id === id ? {
      ...e,
      ...patch
    } : e)
  });
  const total = current.reduce((s, e) => s + monthlyFor(e), 0);
  return <StepFrame title="What do you earn?" description="Use your pay before taxes. Your paystub will confirm it later." help={<HelpPanel question="Should I include my bonus?" data-id="element-278" data-mp="1jkvnip1lmp52">
          Yes — if you’ve received it regularly for the last two years, it can usually count toward your income. Enter your yearly average.
        </HelpPanel>} data-id="element-277" data-mp="1em2cr4dp5c8l">
      {current.map(e => <section key={e.id} aria-label={`Pay at ${e.name}`} className="mb-6" data-id="element-279" data-mp="3e25ho6vc0mwx">
          <p className="mb-3 text-sm font-medium text-ink-700" data-id="element-280" data-mp="3932aafrvq4ls">{e.name || 'Your employer'}</p>
          <div role="radiogroup" aria-label="Pay frequency" className="mb-3 inline-flex rounded-xl bg-ink-100 p-1" data-id="element-281" data-mp="343fz0bvm2yfm">
            {(['annual', 'monthly', 'hourly'] as const).map(f => <button key={f} role="radio" aria-checked={e.payFrequency === f} onClick={() => setEmployer(e.id, {
          payFrequency: f
        })} className={`h-9 rounded-lg px-4 text-sm font-medium transition-[background-color,color,box-shadow] duration-150 ${e.payFrequency === f ? 'bg-white text-ink-900 shadow-soft' : 'text-ink-500 hover:text-ink-700'}`} data-id="element-282" data-mp="2z6z859x125yu">
                {f === 'annual' ? 'Yearly' : f === 'monthly' ? 'Monthly' : 'Hourly'}
              </button>)}
          </div>
          <TextField label="Base pay" prefix="$" suffix={e.payFrequency === 'annual' ? 'per year' : e.payFrequency === 'monthly' ? 'per month' : 'per hour'} inputMode="numeric" value={formatAmountInput(e.basePay)} onChange={v => setEmployer(e.id, {
        basePay: v.replace(/[^0-9]/g, '')
      })} data-id="element-283" data-mp="elfiaqglxgrq" />
          <p className="mb-2.5 mt-6 text-sm font-medium text-ink-700" data-id="element-284" data-mp="3f3rjjtrh8fqj">Do you also earn…</p>
          <div className="flex flex-wrap gap-2" data-id="element-285" data-mp="6o7wjcheftjx">
            {extras.map(x => {
          const on = shown[`${e.id}-${x.key}`] || !!e[x.key];
          return <button key={x.key} aria-pressed={on} onClick={() => {
            setShown(s => ({
              ...s,
              [`${e.id}-${x.key}`]: !on
            }));
            if (on) setEmployer(e.id, {
              [x.key]: ''
            } as Partial<Employer>);
          }} className={`inline-flex h-10 items-center gap-1.5 rounded-full px-4 text-sm font-medium ring-1 ring-inset transition-[background-color,color,box-shadow] duration-150 ${on ? 'bg-pulse-600 text-white ring-pulse-600' : 'bg-white text-ink-700 ring-ink-200 hover:ring-ink-300'}`} data-id="element-286" data-mp="3suwb08koy1ps">
                  {on ? <CheckIcon className="h-3.5 w-3.5" aria-hidden data-id="element-287" data-mp="2nuetk26y60t5" /> : <PlusIcon className="h-3.5 w-3.5" aria-hidden data-id="element-288" data-mp="2iwdedscn6lmy" />}
                  {x.label}
                </button>;
        })}
          </div>
          <div className="mt-2 space-y-3" data-id="element-289" data-mp="1v7bzbm9b0c8p">
            <AnimatePresence initial={false} data-id="element-290" data-mp="1r576uquldlnn">
              {extras.filter(x => shown[`${e.id}-${x.key}`] || !!e[x.key]).map(x => <motion.div key={x.key} initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease
          }} className="overflow-hidden" data-id="element-291" data-mp="24youamqefiiy">
                    <div className="pt-2" data-id="element-292" data-mp="17h7whh3xwgir">
                      <TextField label={`${x.label} (yearly average)`} prefix="$" inputMode="numeric" value={formatAmountInput(e[x.key])} onChange={v => setEmployer(e.id, {
                [x.key]: v.replace(/[^0-9]/g, '')
              } as Partial<Employer>)} data-id="element-293" data-mp="17ggdd5rj4b9d" />
                    </div>
                  </motion.div>)}
            </AnimatePresence>
          </div>
        </section>)}

      <div className="flex items-center justify-between rounded-2xl bg-ink-900 px-5 py-4 text-white" data-id="element-294" data-mp="36fq5x72quham">
        <span className="text-sm text-white/60" data-id="element-295" data-mp="31gdgcekhmw4c">Monthly income</span>
        <AnimatedNumber value={total} className="text-2xl font-semibold tracking-tight" data-id="element-296" data-mp="2wpdk7bj70i2w" />
      </div>
    </StepFrame>;
}
const incomeTypes = ['Rental income', 'Social Security', 'Pension', 'Child support', 'Alimony', 'Disability', 'Other'];
export function OtherIncomeStep() {
  const {
    answers,
    update
  } = usePortal();
  const list = answers.otherIncome;
  return <StepFrame title="Any other income?" description="Add anything you’d like us to consider. Skip this if it doesn’t apply." help={<HelpPanel question="Do I have to include child support or alimony?" data-id="element-298" data-mp="t90av0teyp1t">
          No. You only need to list child support, alimony, or separate maintenance if you want it counted toward qualifying for your loan.
        </HelpPanel>} data-id="element-297" data-mp="2ioszuaxmqe7a">
      <div className="flex flex-wrap gap-2" data-id="element-299" data-mp="1272fdqccvztu">
        {incomeTypes.map(t => <button key={t} onClick={() => update({
        otherIncome: [...list, {
          id: uid('i'),
          type: t,
          monthly: ''
        }]
      })} className="h-10 rounded-full bg-white px-4 text-sm font-medium text-ink-700 ring-1 ring-inset ring-ink-200 transition-[box-shadow,background-color] duration-150 hover:bg-ink-50 hover:ring-ink-300" data-id="element-300" data-mp="177q4u7kxs8ly">
            + {t}
          </button>)}
      </div>
      <div className="mt-6 space-y-3" data-id="element-301" data-mp="obgvtyow688t">
        <AnimatePresence initial={false} data-id="element-302" data-mp="3mzxkvn1c6vkd">
          {list.map(i => <motion.div key={i.id} layout initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.2,
          ease
        }} className="flex items-end gap-3 rounded-2xl bg-white p-4 ring-1 ring-ink-200" data-id="element-303" data-mp="3ryh6fmc0q5c7">
              <TextField className="flex-1" label={i.type} prefix="$" suffix="/ month" inputMode="numeric" value={formatAmountInput(i.monthly)} onChange={v => update({
            otherIncome: list.map(x => x.id === i.id ? {
              ...x,
              monthly: v.replace(/[^0-9]/g, '')
            } : x)
          })} data-id="element-304" data-mp="u44e8oing722" />
              <button onClick={() => update({
            otherIncome: list.filter(x => x.id !== i.id)
          })} className="grid h-12 w-12 place-items-center rounded-xl text-ink-400 transition-colors duration-150 hover:bg-ink-100 hover:text-critical-500" aria-label={`Remove ${i.type}`} data-id="element-305" data-mp="b7cw4ieufxzl">
                <Trash2Icon className="h-4 w-4" data-id="element-306" data-mp="20kgbmndndkwl" />
              </button>
            </motion.div>)}
        </AnimatePresence>
        {list.length === 0 && <p className="text-sm text-ink-400" data-id="element-307" data-mp="1vnzdd0cqhq5s">Nothing added — that’s perfectly fine.</p>}
      </div>
    </StepFrame>;
}